import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { useEffect } from "react";
import { useWatch } from "react-hook-form";
import { FormFieldOverride } from "types/enums";

import {
    FormFieldOverrideContext,
    FORM_FIELD_OVERRIDE_MAPPINGS,
    GLOBAL_UNSUPPORTED_OVERRIDES,
    normalizeOverrideValue,
} from "./formFieldOverrides";

export const useFormFieldOverrides = (propertyPath: string, overrideContext?: FormFieldOverrideContext) => {
    const { control, getValues, setValue } = useGeneralForm();
    const parentFormFieldId = useWatch({ control, name: `${propertyPath}.parent_form_field_id` }) as string | null;
    const globalFormFieldId = useWatch({ control, name: `${propertyPath}.global_form_field_id` }) as string | null;
    const watchedOverrides = useWatch({ control, name: `${propertyPath}.overridden_attributes` }) as
        | FormFieldOverride[]
        | undefined;
    const watchedOwnValues = useWatch({ control, name: `${propertyPath}.override_own_values` }) as
        | Record<string, unknown>
        | undefined;
    const watchedSourceValues = useWatch({ control, name: `${propertyPath}.override_source_values` }) as
        | Record<string, unknown>
        | null
        | undefined;
    const isSourceLinked = Boolean(parentFormFieldId || globalFormFieldId);
    const sourceValues =
        watchedSourceValues !== undefined ? watchedSourceValues : (overrideContext?.source_values ?? null);
    const ownValues = watchedOwnValues ?? overrideContext?.own_values ?? {};
    const overriddenAttributes = watchedOverrides ?? overrideContext?.overridden_attributes ?? [];

    useEffect(() => {
        if (!isSourceLinked || !overrideContext) return;

        if (getValues(`${propertyPath}.overridden_attributes`) === undefined) {
            setValue(`${propertyPath}.overridden_attributes`, overrideContext.overridden_attributes, {
                shouldDirty: false,
            });
        }
        if (getValues(`${propertyPath}.override_own_values`) === undefined) {
            setValue(`${propertyPath}.override_own_values`, overrideContext.own_values, { shouldDirty: false });
        }
        if (getValues(`${propertyPath}.override_source_values`) === undefined) {
            setValue(`${propertyPath}.override_source_values`, overrideContext.source_values, { shouldDirty: false });
        }
    }, [getValues, isSourceLinked, overrideContext, propertyPath, setValue]);

    const isOverrideAvailable = (override: FormFieldOverride) =>
        isSourceLinked && sourceValues !== null && !(globalFormFieldId && GLOBAL_UNSUPPORTED_OVERRIDES.has(override));
    const isOverridden = (override: FormFieldOverride) => overriddenAttributes.includes(override);

    const toggleOverride = (override: FormFieldOverride) => {
        const mappings = FORM_FIELD_OVERRIDE_MAPPINGS[override];
        const setValueOptions = { shouldDirty: true, shouldValidate: true };

        if (isOverridden(override)) {
            const nextOwnValues = { ...ownValues };
            mappings.forEach(({ formProperty, contextProperty }) => {
                nextOwnValues[contextProperty] = getValues(`${propertyPath}.${formProperty}`);
                if (sourceValues && contextProperty in sourceValues) {
                    setValue(
                        `${propertyPath}.${formProperty}`,
                        normalizeOverrideValue(override, sourceValues[contextProperty]),
                        setValueOptions
                    );
                }
            });
            setValue(`${propertyPath}.override_own_values`, nextOwnValues, setValueOptions);
            setValue(
                `${propertyPath}.overridden_attributes`,
                overriddenAttributes.filter((attribute) => attribute !== override),
                setValueOptions
            );
            return;
        }

        mappings.forEach(({ formProperty, contextProperty }) => {
            if (!(contextProperty in ownValues)) return;

            setValue(
                `${propertyPath}.${formProperty}`,
                normalizeOverrideValue(override, ownValues[contextProperty]),
                setValueOptions
            );
        });
        setValue(
            `${propertyPath}.overridden_attributes`,
            [...new Set([...overriddenAttributes, override])],
            setValueOptions
        );
    };

    return {
        isSourceLinked,
        isOverrideAvailable,
        isOverridden,
        toggleOverride,
    };
};
